import React, { useState, CSSProperties } from 'react'
import FieldActions from './FieldActions'

interface FieldNodeWrapperProps {
  fieldId: string
  isSelected: boolean
  isRemovable?: boolean
  onSelect: (fieldId: string) => void
  onDelete?: (fieldId: string) => void
  children: React.ReactNode
}

/**
 * Wrapper for editable fields in configuration mode
 * - Shows outline on hover (light blue)
 * - Shows solid outline when selected
 * - Shows action buttons when selected (for removable fields)
 * - Handles field selection
 *
 * Pattern inspired by email builder's EditorBlockWrapper
 */
export default function FieldNodeWrapper({
  fieldId,
  isSelected,
  isRemovable = false,
  onSelect,
  onDelete,
  children
}: FieldNodeWrapperProps) {
  const [mouseInside, setMouseInside] = useState(false)

  let outline: CSSProperties['outline']
  let outlineOffset: CSSProperties['outlineOffset']

  if (isSelected) {
    // Solid blue outline when selected
    outline = '2px solid rgb(59, 130, 246)' // blue-500
    outlineOffset = '-2px'
  } else if (mouseInside) {
    // Light blue outline on hover
    outline = '2px solid rgba(59, 130, 246, 0.3)'
    outlineOffset = '-2px'
  }

  const handleClick = (e: React.MouseEvent) => {
    // Select this field
    onSelect(fieldId)
    e.stopPropagation()
  }

  return (
    <div
      className="relative"
      style={{
        outline,
        outlineOffset,
        borderRadius: '4px',
        transition: 'outline 150ms ease-in-out',
      }}
      onMouseEnter={(e) => {
        setMouseInside(true)
        e.stopPropagation()
      }}
      onMouseLeave={() => {
        setMouseInside(false)
      }}
      onClick={handleClick}
    >
      {/* Action buttons (delete, drag) */}
      {isSelected && isRemovable && onDelete && (
        <FieldActions
          fieldId={fieldId}
          isRemovable={isRemovable}
          onDelete={onDelete}
        />
      )}

      {children}
    </div>
  )
}
